import { useEffect, useState } from 'react';

import { useAccountCompanyId } from 'application/hooks';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';

import {
  useBillingInfo,
  useExtendSubscription,
  useLaunchSubscription,
  useLicensesSubscriptionCost,
} from '@entities/billing';
import { PaymentKind } from '@shared/api/graphql/generated';

import { ExtendSubscriptionModalProps } from '../ui/ExtendSubscriptionModal/extendSubscriptionModalProps';

interface Props extends ExtendSubscriptionModalProps {
  close: (count?: number) => void;
}

interface UseExtendSubscriptionModal {
  subscriptionCost: number;
  licenseSubscriptionLoading: boolean;
  isSbbolPayment: boolean;
  onExtendSubscriptionConfirm: () => Promise<void>;
}

const useExtendSubscriptionModal = ({ licenses, close, submitCallback }: Props): UseExtendSubscriptionModal => {
  const companyId = useAccountCompanyId();

  const { getSubscriptionCost } = useLicensesSubscriptionCost(companyId);

  const { request: extendSubscriptionRequest, result: extendSubscriptionResult } = useExtendSubscription();
  const { request: launchSubscriptionRequest, result: launchSubscriptionResult } = useLaunchSubscription();

  const licenseSubscriptionLoading = extendSubscriptionResult.loading || launchSubscriptionResult.loading;

  const { hasInactiveLicenses, selectedPaymentKind } = useBillingInfo(companyId);

  const [subscriptionCost, setSubscriptionCost] = useState<number>(0);

  useEffect(() => {
    const setCost = async () => {
      if (licenses.length !== 0) {
        const cost = await getSubscriptionCost(licenses);

        setSubscriptionCost(cost);
      }
    };

    setCost();
  }, []);

  const onExtendSubscriptionConfirm = async () => {
    AnalyticsService.event(EventNameAMP.BILLING_LICENSES_PAY_CONFIRM_CLICKED);

    if ((licenses?.length || 0) > 0) {
      if (hasInactiveLicenses) {
        await launchSubscriptionRequest(companyId, licenses);
      } else {
        await extendSubscriptionRequest(companyId, licenses);
      }
    }

    submitCallback?.();
    close(2);
  };

  return {
    subscriptionCost,
    licenseSubscriptionLoading,
    isSbbolPayment: selectedPaymentKind === PaymentKind.Sbbol,
    onExtendSubscriptionConfirm,
  };
};

export default useExtendSubscriptionModal;
